import { useLayoutEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'

import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import type { CryptoRedemptionOption } from '@/api/gen'
import { useRedemptionsRedemptionOptionsCryptoList } from '@/api/gen'
import { Loader } from '@/components/loader'
import { useEdgeBalance } from '@/hooks/use-edge-balance'
import { redemptionListQueryOptions } from '@/pages/rewards/redemption-query-options'

import { CryptoRedeemModal } from './crypto-redeem-modal'
import { RedeemAmountField } from './redeem-amount-field'
import { RedeemOptionCard } from './redeem-option-card'

export const CryptoRedeemForm = () => {
  const { balance } = useEdgeBalance()
  const { data, isPending } = useRedemptionsRedemptionOptionsCryptoList({
    query: {
      ...redemptionListQueryOptions,
      refetchOnMount: false,
    },
  })

  const [selectedCoin, setSelectedCoin] = useState<CryptoRedemptionOption | null>(null)
  const [edgeToRedeem, setEdgeToRedeem] = useState(0)
  const [showModal, setShowModal] = useState(false)

  useLayoutEffect(
    function syncSelectedCoinWithOptions() {
      if (data === undefined || data.length === 0) {
        return
      }
      setSelectedCoin((prev) => {
        if (prev && data.some((c) => c.id === prev.id)) {
          return prev
        }

        return data[0] ?? null
      })
    },
    [data],
  )

  const onChangeCoin = (coin: CryptoRedemptionOption) => {
    if (selectedCoin?.id !== coin.id) {
      setSelectedCoin(coin)
    }
  }

  const onToggleModal = () => {
    setShowModal((prev) => !prev)
  }

  if (isPending) {
    return <Loader />
  }
  if (!data || data.length === 0) {
    return (
      <Typography
        color="text.secondary"
        variant="body3"
      >
        <FormattedMessage id="edge.premiumRedeem.noCryptoOptions" />
      </Typography>
    )
  }

  const isAmountInvalid =
    selectedCoin !== null &&
    edgeToRedeem > 0 &&
    (edgeToRedeem < (selectedCoin.min_redemption_edge ?? 0) || edgeToRedeem > balance)

  return (
    <div className="flex flex-col gap-6">
      <div className="flex gap-2 overflow-x-auto pb-2">
        {data.map((option) => (
          <RedeemOptionCard
            key={option.id}
            icon={option.icon}
            isActive={selectedCoin?.id === option.id}
            name={option.cryptocurrency}
            onClick={() => {
              onChangeCoin(option)
            }}
          />
        ))}
      </div>
      <div className="flex gap-4 sm:flex-col sm:gap-8">
        <RedeemAmountField
          isInvalid={isAmountInvalid}
          value={edgeToRedeem}
          helper={
            edgeToRedeem > balance ? (
              <FormattedMessage
                id="edge.premiumRedeem.insufficientBalance"
                values={{ balance }}
              />
            ) : selectedCoin ? (
              <FormattedMessage
                id="edge.premiumRedeem.minimumAmount"
                values={{ amount: selectedCoin.min_redemption_edge ?? 0 }}
              />
            ) : (
              ''
            )
          }
          onValueChange={setEdgeToRedeem}
        />
        <Button
          className="flex-[0_0_180px] sm:flex-[1_0_auto]"
          size="large"
          variant="contained"
          disabled={
            data.length === 0 ||
            selectedCoin === null ||
            edgeToRedeem < (selectedCoin.min_redemption_edge ?? 0) ||
            edgeToRedeem > balance
          }
          onClick={onToggleModal}
        >
          <FormattedMessage id="common.controls.continue" />
        </Button>
      </div>
      {selectedCoin && (
        <CryptoRedeemModal
          coin={selectedCoin}
          edge={edgeToRedeem}
          open={showModal}
          onClose={onToggleModal}
        />
      )}
    </div>
  )
}
